import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import { toast } from 'sonner';
import { 
  Save, 
  Eye, 
  LogOut, 
  Image as ImageIcon, 
  Type, 
  Palette,
  Mail,
  Link as LinkIcon,
  Plus,
  Trash2
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const API = `${BACKEND_URL}/api`;

const CEOPanel = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({
    // Brand
    brandName: 'JG Models Agency',
    brandTagline: 'Transformamos modelos en marcas rentables',
    logoUrl: 'https://customer-assets.emergentagent.com/job_talentmgmt/artifacts/dyyu0c3k_Disen%CC%83o%20sin%20ti%CC%81tulo.png',
    
    // Hero Section
    heroTitle: 'La agencia #1 de OnlyFans',
    heroSubtitle: 'Transformamos modelos en marcas rentables. ¡Únete y forma parte del top 0,1 % de creadoras!',
    heroImage: 'https://customer-assets.emergentagent.com/job_talentmgmt/artifacts/1uka7rcz_IMG_6781.JPG',
    heroCTAText: 'Postular Ahora',
    
    // Social Proof
    modelsCount: '22+',
    modelsText: 'Creadoras confían en nosotros',
    topPercentage: 'Top 0.1%',
    topPercentageText: 'De creadoras exitosas',
    
    // Quote Section
    quoteText: 'Fundada por un visionario que conoce la industria, JG Models representa apoyo genuino, crecimiento inteligente y éxito sostenible.',
    quoteAuthor: 'Jhosep J.',
    quoteRole: 'Fundador, JG Models Agency',
    
    // Why Choose Us
    whySectionTitle: '¿Por qué elegir JGM?',
    whySectionSubtitle: 'Excelencia y profesionalismo',
    whySection: [
      {
        title: 'Te protegemos',
        description: 'Lo que no publiquemos se mantiene privado. Eliminamos filtraciones antes de que se difundan y mantenemos contacto directo con las plataformas. Tenemos experiencia directa en la industria y sabemos lo importante que es la seguridad real para las creadoras.',
        image: 'https://customer-assets.emergentagent.com/job_talentmgmt/artifacts/fvji2v5y_IMG_6783.JPG'
      },
      {
        title: 'Somos más rápidos',
        description: 'Mientras duermes, nosotros trabajamos en tu éxito. Nuestro equipo gestiona tus chats 24/7, interactúa activamente, vende eficazmente y te respalda. Puedes concentrarte completamente en tu contenido mientras nosotros nos ocupamos de todo detrás de escena.',
        image: 'https://customer-assets.emergentagent.com/job_talentmgmt/artifacts/va1m8btv_IMG_6784.JPG'
      },
      {
        title: 'Somos competitivos',
        description: 'Trabajamos de la mano de altas exponentes de la industria. Nuestra red de contactos y experiencia nos permite ofrecerte las mejores oportunidades y estrategias para maximizar tu potencial y convertirte en una referente del sector.',
        image: 'https://customer-assets.emergentagent.com/job_talentmgmt/artifacts/fxax7thb_IMG_6785.JPG'
      }
    ],
    
    // Comparison Section
    comparisonTitle: '¿Por qué elegirnos sobre otros?',
    comparisonSubtitle: 'Mira la comparación con otras agencias',
    comparisonFeatures: [
      {
        feature: 'Gestión profesional',
        jgm: '100% profesionales capacitados',
        others: '99% dirigidas por hombres sin experiencia'
      },
      {
        feature: 'Soporte',
        jgm: 'Soporte 24/7 personalizado',
        others: 'Soporte genérico o inexistente'
      },
      {
        feature: 'Costos iniciales',
        jgm: 'Sin costos de configuración',
        others: 'Hasta USD 20.000 de tarifa inicial'
      },
      {
        feature: 'Calendario de contenido',
        jgm: 'Nuestros directores creativos establecen un calendario que se adapta a ti',
        others: 'Presión enorme por generar mucho contenido todas las semanas'
      }
    ],
    
    // Process Section
    processTitle: 'Nuestro Proceso',
    processSubtitle: 'Una colaboración simple y transparente en solo tres pasos.',
    processSteps: [
      {
        number: '01',
        title: 'Contacto inicial',
        description: 'Contáctanos por WhatsApp y te responderemos personalmente para conocer tu perfil y evaluar tu potencial en la industria.',
        image: 'https://customer-assets.emergentagent.com/job_talentmgmt/artifacts/2vcae96x_IMG_6786.JPG'
      },
      {
        number: '02',
        title: 'Estrategia personalizada',
        description: 'Desarrollamos un plan a medida para ti, preparamos tu perfil profesionalmente y nos encargamos de toda la configuración necesaria.',
        image: 'https://customer-assets.emergentagent.com/job_talentmgmt/artifacts/nr38ljsv_IMG_6787.JPG'
      },
      {
        number: '03',
        title: 'Crecimiento continuo',
        description: 'Desde el primer día te acompañamos estratégicamente: gestionamos chats, planificamos contenido y potenciamos tus ingresos con JG Models.',
        image: 'https://customer-assets.emergentagent.com/job_talentmgmt/artifacts/tahybhtf_IMG_8733.JPEG'
      }
    ],
    
    // FAQ
    faqTitle: 'Preguntas frecuentes',
    faqSubtitle: 'Encuentra respuestas rápidas a las preguntas más comunes sobre JG Models.',
    faqs: [
      {
        question: '¿Cómo funciona la colaboración con JG Models?',
        answer: 'Después de contactarnos, evaluamos tu perfil y potencial. Si encajas con nuestra visión, comenzamos con una incorporación personalizada donde te guiamos en cada paso del proceso.'
      },
      {
        question: '¿Cuánto cuesta trabajar con JG Models?',
        answer: 'No pagas costos fijos iniciales. Trabajamos con un modelo basado en resultados — transparente, justo y alineado con tu éxito.'
      },
      {
        question: '¿Qué tan seguro está mi contenido con ustedes?',
        answer: 'Tu seguridad es nuestra máxima prioridad. Implementamos sistemas de protección, prevenimos filtraciones, trabajamos directamente con las plataformas y nos aseguramos de que tu contenido esté completamente protegido.'
      },
      {
        question: '¿Quién me apoyará en el proceso?',
        answer: 'Tendrás un equipo dedicado de profesionales que conocen la industria a fondo y están disponibles 24/7 para responder todas tus dudas y apoyarte en cada etapa.'
      },
      {
        question: '¿Qué necesito para empezar con JG Models?',
        answer: 'Solo necesitas compromiso, disciplina y tu celular. Nosotros nos ocupamos del resto: configuración técnica, estrategia de contenido, gestión de chats y todo lo necesario para tu éxito.'
      },
      {
        question: '¿Qué hace diferente a JG Models de otras agencias?',
        answer: 'Trabajamos con 100% profesionales capacitados, ofrecemos soporte 24/7 personalizado, sin costos iniciales, y colaboramos con las más altas exponentes de la industria para garantizar tu crecimiento.'
      }
    ],
    
    // Final CTA
    finalCtaTitle: 'Únete al top 0.1% de creadoras',
    finalCtaHighlight: 'Tu próximo capítulo comienza aquí. Trabajemos juntos para construir tu marca y maximizar tu potencial.',
    finalCtaImage: '',
    
    // Colors
    accentColor: '#c8784e',
    accentColorHover: '#d89a6f',
    
    // Contact
    contactEmail: 'gestion2jgmodels.com',
    applicationFormUrl: 'https://api.whatsapp.com/send?phone=573011920219'
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${API}/landing-config`, {
        withCredentials: true
      });
      if (response.data && Object.keys(response.data).length > 0) {
        // Mezclar defaults con datos de BD para que campos faltantes mantengan sus defaults
        setConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.log('No hay configuración guardada, usando valores por defecto');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/landing-config`, config, {
        withCredentials: true
      });
      toast.success('¡Configuración guardada exitosamente!');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Error al guardar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    navigate('/');
  };

  const updateWhySection = (index, field, value) => {
    const newWhySection = [...config.whySection];
    newWhySection[index][field] = value;
    setConfig({ ...config, whySection: newWhySection });
  };

  const updateProcessStep = (index, field, value) => {
    const newProcessSteps = [...config.processSteps];
    newProcessSteps[index][field] = value;
    setConfig({ ...config, processSteps: newProcessSteps });
  };

  const updateComparisonFeature = (index, field, value) => {
    const newFeatures = [...config.comparisonFeatures];
    newFeatures[index][field] = value;
    setConfig({ ...config, comparisonFeatures: newFeatures });
  };

  const addComparisonFeature = () => {
    setConfig({
      ...config,
      comparisonFeatures: [...config.comparisonFeatures, { feature: '', jgm: '', others: '' }]
    });
  };

  const removeComparisonFeature = (index) => {
    const newFeatures = config.comparisonFeatures.filter((_, i) => i !== index);
    setConfig({ ...config, comparisonFeatures: newFeatures });
  };

  const addFAQ = () => {
    setConfig({
      ...config,
      faqs: [...config.faqs, { question: '', answer: '' }]
    });
  };

  const removeFAQ = (index) => {
    const newFaqs = config.faqs.filter((_, i) => i !== index);
    setConfig({ ...config, faqs: newFaqs });
  };

  const updateFAQ = (index, field, value) => {
    const newFaqs = [...config.faqs];
    newFaqs[index][field] = value;
    setConfig({ ...config, faqs: newFaqs });
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-black border-b border-gray-800 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#c8784e]">CEO Panel</h1>
              <p className="text-sm text-gray-400">Editor de Landing Page</p>
            </div>
            
            <div className="flex items-center gap-4">
              <Button
                onClick={() => window.open('/', '_blank')}
                variant="outline"
                className="border-gray-700 text-white hover:bg-gray-800"
              >
                <Eye className="mr-2 h-4 w-4" />
                Vista Previa
              </Button>
              
              <Button
                onClick={handleSave}
                disabled={loading}
                className="bg-[#c8784e] hover:bg-[#d89a6f] text-black"
              >
                <Save className="mr-2 h-4 w-4" />
                {loading ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
              
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-gray-700 text-white hover:bg-gray-800"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Salir
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid gap-6">
          
          {/* Brand Section */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Type size={24} className="text-[#c8784e]" />
                Marca
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-300">Nombre de la Agencia</Label>
                <Input
                  value={config.brandName}
                  onChange={(e) => setConfig({ ...config, brandName: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">Logo (subir desde PC)</Label>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setLoading(true);
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const response = await axios.post(`${API}/upload-image`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' },
                          withCredentials: true
                        });
                        const imageUrl = `${BACKEND_URL}${response.data.url}`;
                        setConfig(prevConfig => ({ ...prevConfig, logoUrl: imageUrl }));
                        toast.success('✅ Logo subido exitosamente');
                        e.target.value = ''; // Reset input
                      } catch (error) {
                        console.error('Error uploading logo:', error);
                        toast.error('❌ Error al subir logo');
                      } finally {
                        setLoading(false);
                      }
                    }
                  }}
                  className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#c8784e] file:text-black hover:file:bg-[#d89a6f] cursor-pointer"
                />
                {config.logoUrl && (
                  <div className="mt-2 p-2 bg-gray-700 rounded">
                    <img src={config.logoUrl} alt="Logo preview" className="h-16 object-contain bg-white p-2 rounded mb-2" />
                    <Button
                      onClick={() => {
                        setConfig(prevConfig => ({ ...prevConfig, logoUrl: '' }));
                        toast.success('Logo eliminado');
                      }}
                      size="sm"
                      variant="outline"
                      className="text-red-500 border-red-500 hover:bg-red-500 hover:text-white"
                    >
                      Eliminar logo
                    </Button>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">Si no subes logo, se mostrará el nombre de la agencia</p>
              </div>
              <div>
                <Label className="text-gray-300">Tagline (Footer)</Label>
                <Input
                  value={config.brandTagline}
                  onChange={(e) => setConfig({ ...config, brandTagline: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </CardContent>
          </Card>

          {/* Hero Section */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <ImageIcon size={24} className="text-[#c8784e]" />
                Sección Hero (Principal)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-300">Título Principal</Label>
                <Input
                  value={config.heroTitle}
                  onChange={(e) => setConfig({ ...config, heroTitle: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">Subtítulo</Label>
                <Textarea
                  value={config.heroSubtitle}
                  onChange={(e) => setConfig({ ...config, heroSubtitle: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                  rows={3}
                />
              </div>
              <div>
                <Label className="text-gray-300">Imagen de Fondo (subir desde PC)</Label>
                <input
                  id="hero-upload"
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setLoading(true);
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const response = await axios.post(`${API}/upload-image`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' },
                          withCredentials: true
                        });
                        const imageUrl = `${BACKEND_URL}${response.data.url}`;
                        setConfig(prevConfig => ({ ...prevConfig, heroImage: imageUrl }));
                        toast.success('✅ Imagen de hero subida exitosamente');
                        e.target.value = '';
                      } catch (error) {
                        console.error('Error uploading hero image:', error);
                        toast.error('❌ Error al subir imagen');
                      } finally {
                        setLoading(false);
                      }
                    }
                  }}
                  className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#c8784e] file:text-black hover:file:bg-[#d89a6f] cursor-pointer"
                />
                {config.heroImage && (
                  <div className="mt-2 p-2 bg-gray-700 rounded">
                    <img src={config.heroImage} alt="Hero preview" className="h-32 w-full object-cover rounded" />
                    <p className="text-xs text-gray-400 mt-1">Vista previa de la imagen actual</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Social Proof */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Type size={24} className="text-[#c8784e]" />
                Prueba Social
              </CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Número de Modelos</Label>
                <Input
                  value={config.modelsCount}
                  onChange={(e) => setConfig({ ...config, modelsCount: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">Texto</Label>
                <Input
                  value={config.modelsText}
                  onChange={(e) => setConfig({ ...config, modelsText: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">Porcentaje Top</Label>
                <Input
                  value={config.topPercentage || 'Top 0.1%'}
                  onChange={(e) => setConfig({ ...config, topPercentage: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">Texto del Porcentaje</Label>
                <Input
                  value={config.topPercentageText || 'De creadoras exitosas'}
                  onChange={(e) => setConfig({ ...config, topPercentageText: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </CardContent>
          </Card>

          {/* Quote Section */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Type size={24} className="text-[#c8784e]" />
                Cita / Testimonio
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-300">Texto de la Cita</Label>
                <Textarea
                  value={config.quoteText}
                  onChange={(e) => setConfig({ ...config, quoteText: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                  rows={3}
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Autor</Label>
                  <Input
                    value={config.quoteAuthor}
                    onChange={(e) => setConfig({ ...config, quoteAuthor: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Rol del Autor</Label>
                  <Input
                    value={config.quoteRole}
                    onChange={(e) => setConfig({ ...config, quoteRole: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Why Choose Us */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Type size={24} className="text-[#c8784e]" />
                ¿Por Qué Elegirnos? (3 Tarjetas)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {config.whySection && config.whySection.map((item, index) => (
                <div key={index} className="border border-gray-700 rounded-lg p-4 space-y-4">
                  <h4 className="text-[#c8784e] font-semibold">Tarjeta {index + 1}</h4>
                  <div>
                    <Label className="text-gray-300">Título</Label>
                    <Input
                      value={item.title}
                      onChange={(e) => updateWhySection(index, 'title', e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Descripción</Label>
                    <Textarea
                      value={item.description}
                      onChange={(e) => updateWhySection(index, 'description', e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Imagen (subir desde PC)</Label>
                    <input
                      id={`why-upload-${index}`}
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setLoading(true);
                          const formData = new FormData();
                          formData.append('file', file);
                          try {
                            const response = await axios.post(`${API}/upload-image`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' },
                              withCredentials: true
                            });
                            const imageUrl = `${BACKEND_URL}${response.data.url}`;
                            const newWhySection = [...config.whySection];
                            newWhySection[index].image = imageUrl;
                            setConfig(prevConfig => ({ ...prevConfig, whySection: newWhySection }));
                            toast.success(`✅ Imagen ${index + 1} subida exitosamente`);
                            e.target.value = '';
                          } catch (error) {
                            console.error('Error uploading image:', error);
                            toast.error('❌ Error al subir imagen');
                          } finally {
                            setLoading(false);
                          }
                        }
                      }}
                      className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#c8784e] file:text-black hover:file:bg-[#d89a6f] cursor-pointer"
                    />
                    {item.image && (
                      <div className="mt-2 p-2 bg-gray-700 rounded">
                        <img src={item.image} alt={`Preview ${index + 1}`} className="h-24 w-full object-cover rounded" />
                        <p className="text-xs text-gray-400 mt-1">Vista previa de la imagen actual</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}

          {/* Process Section */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-white">
                  <Type size={24} className="text-[#c8784e]" />
                  Nuestro Proceso (3 Pasos)
                </CardTitle>
                {(!config.processSteps || config.processSteps.length === 0) && (
                  <Button
                    onClick={() => {
                      setConfig({
                        ...config,
                        processSteps: [
                          {
                            number: "01",
                            title: "Contacto inicial",
                            description: "Contáctanos por WhatsApp y te responderemos personalmente para conocer tu perfil y evaluar tu potencial en la industria.",
                            image: "https://customer-assets.emergentagent.com/job_talentmgmt/artifacts/2vcae96x_IMG_6786.JPG"
                          },
                          {
                            number: "02",
                            title: "Estrategia personalizada",
                            description: "Desarrollamos un plan a medida para ti, preparamos tu perfil profesionalmente y nos encargamos de toda la configuración necesaria.",
                            image: "https://customer-assets.emergentagent.com/job_talentmgmt/artifacts/nr38ljsv_IMG_6787.JPG"
                          },
                          {
                            number: "03",
                            title: "Crecimiento continuo",
                            description: "Desde el primer día te acompañamos estratégicamente: gestionamos chats, planificamos contenido y potenciamos tus ingresos con JG Models.",
                            image: "https://customer-assets.emergentagent.com/job_talentmgmt/artifacts/tahybhtf_IMG_8733.JPEG"
                          }
                        ]
                      });
                      toast.success('✅ Pasos por defecto cargados');
                    }}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    🔄 Cargar Valores por Defecto
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300">Título de la Sección</Label>
                  <Input
                    value={config.processTitle || ''}
                    onChange={(e) => setConfig({ ...config, processTitle: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="Nuestro Proceso"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Subtítulo</Label>
                  <Input
                    value={config.processSubtitle || ''}
                    onChange={(e) => setConfig({ ...config, processSubtitle: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="Una colaboración simple y transparente..."
                  />
                </div>
              </div>

              {(!config.processSteps || config.processSteps.length === 0) && (
                <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4 text-center">
                  <p className="text-yellow-300 mb-3">No hay pasos del proceso. Click en "🔄 Cargar Valores por Defecto" arriba.</p>
                </div>
              )}

              {config.processSteps && config.processSteps.map((step, index) => (
                <div key={index} className="border border-gray-700 rounded-lg p-4 space-y-4">
                  <h4 className="text-[#c8784e] font-semibold">Paso {index + 1}</h4>
                  <div>
                    <Label className="text-gray-300">Título</Label>
                    <Input
                      value={step.title}
                      onChange={(e) => updateProcessStep(index, 'title', e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Descripción</Label>
                    <Textarea
                      value={step.description}
                      onChange={(e) => updateProcessStep(index, 'description', e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Imagen (subir desde PC)</Label>
                    <input
                      id={`process-upload-${index}`}
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setLoading(true);
                          const formData = new FormData();
                          formData.append('file', file);
                          try {
                            const response = await axios.post(`${API}/upload-image`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' },
                              withCredentials: true
                            });
                            const imageUrl = `${BACKEND_URL}${response.data.url}`;
                            const newProcessSteps = [...config.processSteps];
                            newProcessSteps[index].image = imageUrl;
                            setConfig(prevConfig => ({ ...prevConfig, processSteps: newProcessSteps }));
                            toast.success(`✅ Imagen paso ${index + 1} subida exitosamente`);
                            e.target.value = '';
                          } catch (error) {
                            console.error('Error uploading image:', error);
                            toast.error('❌ Error al subir imagen');
                          } finally {
                            setLoading(false);
                          }
                        }
                      }}
                      className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#c8784e] file:text-black hover:file:bg-[#d89a6f] cursor-pointer"
                    />
                    {step.image && (
                      <div className="mt-2 p-2 bg-gray-700 rounded">
                        <img src={step.image} alt={`Preview ${index + 1}`} className="h-24 w-full object-cover rounded" />
                        <p className="text-xs text-gray-400 mt-1">Vista previa de la imagen actual</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>


          {/* Comparison Section */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-white">
                  <Type size={24} className="text-[#c8784e]" />
                  Tabla de Comparación
                </CardTitle>
                <div className="flex gap-2">
                  {(!config.comparisonFeatures || config.comparisonFeatures.length === 0) && (
                    <Button
                      onClick={() => {
                        setConfig({
                          ...config,
                          comparisonFeatures: [
                            {
                              feature: 'Gestión profesional',
                              jgm: '100% profesionales capacitados',
                              others: '99% dirigidas por hombres sin experiencia'
                            },
                            {
                              feature: 'Soporte',
                              jgm: 'Soporte 24/7 personalizado',
                              others: 'Soporte genérico o inexistente'
                            },
                            {
                              feature: 'Costos iniciales',
                              jgm: 'Sin costos de configuración',
                              others: 'Hasta USD 20.000 de tarifa inicial'
                            },
                            {
                              feature: 'Calendario de contenido',
                              jgm: 'Nuestros directores creativos establecen un calendario que se adapta a ti',
                              others: 'Presión enorme por generar mucho contenido todas las semanas'
                            }
                          ]
                        });
                        toast.success('✅ Filas por defecto cargadas');
                      }}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      🔄 Cargar Valores por Defecto
                    </Button>
                  )}
                  <Button
                    onClick={addComparisonFeature}
                    size="sm"
                    className="bg-[#c8784e] hover:bg-[#d89a6f] text-black"
                  >
                    + Agregar Fila
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300">Título</Label>
                  <Input
                    value={config.comparisonTitle || ''}
                    onChange={(e) => setConfig({ ...config, comparisonTitle: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="¿Por qué elegirnos sobre otros?"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Subtítulo</Label>
                  <Input
                    value={config.comparisonSubtitle || ''}
                    onChange={(e) => setConfig({ ...config, comparisonSubtitle: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="Mira la comparación con otras agencias"
                  />
                </div>
              </div>

              {(!config.comparisonFeatures || config.comparisonFeatures.length === 0) && (
                <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4 text-center">
                  <p className="text-yellow-300 mb-3">No hay filas de comparación. Click en "+ Agregar Fila" para crear la primera.</p>
                </div>
              )}

              {config.comparisonFeatures && config.comparisonFeatures.map((item, index) => (
                <div key={index} className="border border-gray-700 rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-[#c8784e] font-semibold">Fila {index + 1}</h4>
                    <Button
                      onClick={() => removeComparisonFeature(index)}
                      size="sm"
                      variant="destructive"
                    >
                      Eliminar
                    </Button>
                  </div>
                  <div>
                    <Label className="text-gray-300">Característica</Label>
                    <Input
                      value={item.feature}
                      onChange={(e) => updateComparisonFeature(index, 'feature', e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="Ej: Gestión profesional"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">JG Models (Lo bueno)</Label>
                    <Textarea
                      value={item.jgm}
                      onChange={(e) => updateComparisonFeature(index, 'jgm', e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white"
                      rows={2}
                      placeholder="Ej: 100% profesionales capacitados"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Otras Agencias (Lo malo)</Label>
                    <Textarea
                      value={item.others}
                      onChange={(e) => updateComparisonFeature(index, 'others', e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white"
                      rows={2}
                      placeholder="Ej: Soporte genérico o inexistente"
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>


            </CardContent>
          </Card>

          {/* FAQs */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-white">
                  <Type size={24} className="text-[#c8784e]" />
                  Preguntas Frecuentes (FAQ)
                </CardTitle>
                <Button
                  onClick={addFAQ}
                  size="sm"
                  className="bg-[#c8784e] hover:bg-[#d89a6f] text-black"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Agregar FAQ
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {config.faqs && config.faqs.map((faq, index) => (
                <div key={index} className="border border-gray-700 rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-[#c8784e] font-semibold">FAQ {index + 1}</h4>
                    <Button
                      onClick={() => removeFAQ(index)}
                      size="sm"
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div>
                    <Label className="text-gray-300">Pregunta</Label>
                    <Input
                      value={faq.question}
                      onChange={(e) => updateFAQ(index, 'question', e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Respuesta</Label>
                    <Textarea
                      value={faq.answer}
                      onChange={(e) => updateFAQ(index, 'answer', e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white"
                      rows={3}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Final CTA */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Type size={24} className="text-[#c8784e]" />
                CTA Final
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-300">Título</Label>
                <Input
                  value={config.finalCtaTitle}
                  onChange={(e) => setConfig({ ...config, finalCtaTitle: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">Texto Destacado</Label>
                <Input
                  value={config.finalCtaHighlight}
                  onChange={(e) => setConfig({ ...config, finalCtaHighlight: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">Imagen de Fondo (subir desde PC)</Label>
                <input
                  id="finalcta-upload"
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setLoading(true);
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const response = await axios.post(`${API}/upload-image`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' },
                          withCredentials: true
                        });
                        const imageUrl = `${BACKEND_URL}${response.data.url}`;
                        setConfig(prevConfig => ({ ...prevConfig, finalCtaImage: imageUrl }));
                        toast.success('✅ Imagen de CTA final subida exitosamente');
                        e.target.value = '';
                      } catch (error) {
                        console.error('Error uploading final CTA image:', error);
                        toast.error('❌ Error al subir imagen');
                      } finally {
                        setLoading(false);
                      }
                    }
                  }}
                  className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#c8784e] file:text-black hover:file:bg-[#d89a6f] cursor-pointer"
                />
                {config.finalCtaImage && (
                  <div className="mt-2 p-2 bg-gray-700 rounded">
                    <img src={config.finalCtaImage} alt="CTA Final preview" className="h-32 w-full object-cover rounded" />
                    <p className="text-xs text-gray-400 mt-1">Vista previa de la imagen actual</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Colors */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Palette size={24} className="text-[#c8784e]" />
                Colores
              </CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Color Principal</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={config.accentColor}
                    onChange={(e) => setConfig({ ...config, accentColor: e.target.value })}
                    className="w-20 h-10 bg-gray-700 border-gray-600"
                  />
                  <Input
                    value={config.accentColor}
                    onChange={(e) => setConfig({ ...config, accentColor: e.target.value })}
                    className="flex-1 bg-gray-700 border-gray-600 text-white"
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-300">Color Hover</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={config.accentColorHover}
                    onChange={(e) => setConfig({ ...config, accentColorHover: e.target.value })}
                    className="w-20 h-10 bg-gray-700 border-gray-600"
                  />
                  <Input
                    value={config.accentColorHover}
                    onChange={(e) => setConfig({ ...config, accentColorHover: e.target.value })}
                    className="flex-1 bg-gray-700 border-gray-600 text-white"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact & Links */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <LinkIcon size={24} className="text-[#c8784e]" />
                Contacto y Enlaces
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-300 flex items-center gap-2">
                  <Mail size={16} />
                  Email de Contacto
                </Label>
                <Input
                  value={config.contactEmail}
                  onChange={(e) => setConfig({ ...config, contactEmail: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                  type="email"
                />
              </div>
              <div>
                <Label className="text-gray-300">URL del Formulario de Aplicación</Label>
                <Input
                  value={config.applicationFormUrl}
                  onChange={(e) => setConfig({ ...config, applicationFormUrl: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                  type="url"
                />
              </div>
            </CardContent>
          </Card>

        </div>

        {/* Save Button (Bottom) */}
        <div className="mt-8 flex justify-center">
          <Button
            onClick={handleSave}
            disabled={loading}
            size="lg"
            className="bg-[#c8784e] hover:bg-[#d89a6f] text-black px-12 py-6 text-lg"
          >
            <Save className="mr-2 h-5 w-5" />
            {loading ? 'Guardando...' : 'Guardar Todos los Cambios'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CEOPanel;
